(function initAlgoInkProductionBundle(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.AlgoInkProductionBundle = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function createApi() {
  const SCHEMA_VERSION = 'algoink.production-bundle/v1';

  function fnv1a(value) {
    const text = String(value ?? '');
    let hash = 2166136261;
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  }

  function stableValue(value) {
    if (Array.isArray(value)) return value.map(stableValue);
    if (!value || typeof value !== 'object') return value;

    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        if (value[key] !== undefined) result[key] = stableValue(value[key]);
        return result;
      }, {});
  }

  function stableStringify(value, spacing = 0) {
    return JSON.stringify(stableValue(value), null, spacing);
  }

  function numberFromAttribute(svg, attribute) {
    const match = String(svg || '').match(new RegExp(`\\b${attribute}=["'](-?(?:\\d+\\.?\\d*|\\.\\d+))`, 'i'));
    return match ? Number(match[1]) : null;
  }

  function inspectSvg(svg) {
    const source = String(svg || '');
    if (!/<svg\b/i.test(source)) throw new Error('Production SVG is missing a valid <svg> root.');

    const viewBoxMatch = source.match(/\bviewBox=["']([^"']+)["']/i);
    const viewBox = viewBoxMatch
      ? viewBoxMatch[1].trim().split(/[\s,]+/).map(Number)
      : [];
    const inkScapeLayers = Array.from(source.matchAll(/\binkscape:label=["']([^"']+)["']/gi))
      .map(match => match[1]);
    const dataLayers = Array.from(source.matchAll(/\bdata-algoink-pen=["']([^"']+)["']/gi))
      .map(match => match[1]);
    const penLayers = (inkScapeLayers.length ? inkScapeLayers : dataLayers)
      .filter((value, index, values) => values.indexOf(value) === index);

    return {
      bytes: typeof TextEncoder === 'function' ? new TextEncoder().encode(source).length : source.length,
      pathCount: (source.match(/<path\b/gi) || []).length,
      polylineCount: (source.match(/<polyline\b/gi) || []).length,
      lineCount: (source.match(/<line\b/gi) || []).length,
      circleCount: (source.match(/<circle\b/gi) || []).length,
      groupCount: (source.match(/<g\b/gi) || []).length,
      rasterImageCount: (source.match(/<image\b/gi) || []).length,
      backgroundRectCount: (source.match(/<rect\b(?=[^>]*\bid=["']background["'])/gi) || []).length,
      productionOnly: /\bdata-algoink-production-only=["']true["']/i.test(source),
      productionLayerSchema: source.match(/\bdata-algoink-production-layers=["']([^"']+)["']/i)?.[1] || null,
      strokeWidth: numberFromAttribute(source, 'stroke-width'),
      viewBox: viewBox.length === 4 && viewBox.every(Number.isFinite) ? {
        minX: viewBox[0],
        minY: viewBox[1],
        width: viewBox[2],
        height: viewBox[3]
      } : null,
      penLayers,
      svgHash: fnv1a(source)
    };
  }

  function compactObject(value) {
    if (Array.isArray(value)) return value.map(compactObject);
    if (!value || typeof value !== 'object') return value;

    return Object.entries(value).reduce((result, [key, item]) => {
      if (item === undefined || item === null || item === '') return result;
      result[key] = compactObject(item);
      return result;
    }, {});
  }

  function createProductionBundle(input = {}) {
    const production = input.production || {};
    const svgInspection = input.svg ? inspectSvg(input.svg) : (production.svgInspection || {});
    const productionHash = production.productionHash || production.hash || svgInspection.svgHash;
    const kind = String(input.kind || '').trim();

    if (!kind) throw new Error('Production Bundle requires a product kind.');
    if (!production.fileName) throw new Error('Production Bundle requires a production SVG file name.');
    if (!productionHash) throw new Error('Production Bundle requires a production hash or SVG content.');

    const createdAt = input.createdAt || new Date().toISOString();
    const baseBundle = compactObject({
      schemaVersion: SCHEMA_VERSION,
      bundleId: `algoink-${kind}-${productionHash}`,
      createdAt,
      workflow: {
        status: input.status || 'customer-approved',
        productionReady: input.productionReady !== false
      },
      product: {
        kind,
        handle: input.product?.handle,
        variantId: input.product?.variantId,
        title: input.product?.title
      },
      algorithm: {
        version: input.algorithm?.version || 'map-svg-v1',
        seed: input.algorithm?.seed
      },
      source: input.source || {},
      preview: input.preview || {},
      production: {
        fileName: production.fileName,
        mimeType: production.mimeType || 'image/svg+xml',
        sizeMM: production.sizeMM,
        strokeWidthMM: production.strokeWidthMM ?? svgInspection.strokeWidth,
        geometry: production.geometry || {
          paths: svgInspection.pathCount,
          polylines: svgInspection.polylineCount,
          lines: svgInspection.lineCount,
          circles: svgInspection.circleCount
        },
        penLayers: production.penLayers || svgInspection.penLayers || [],
        svgProfile: production.svgProfile || {
          productionOnly: svgInspection.productionOnly,
          productionLayerSchema: svgInspection.productionLayerSchema,
          rasterImages: svgInspection.rasterImageCount,
          backgroundRects: svgInspection.backgroundRectCount
        },
        hashes: {
          production: productionHash,
          svg: production.svgHash || svgInspection.svgHash,
          path: production.pathHash
        }
      },
      customization: input.customization || {},
      provenance: input.provenance || {}
    });

    return {
      ...baseBundle,
      integrity: {
        algorithm: 'fnv1a-32',
        bundleHash: fnv1a(stableStringify(baseBundle))
      }
    };
  }

  function toBundleJSON(bundle) {
    return `${stableStringify(bundle, 2)}\n`;
  }

  return {
    SCHEMA_VERSION,
    createProductionBundle,
    fnv1a,
    inspectSvg,
    stableStringify,
    toBundleJSON
  };
}));
